import { AlertFactory } from 'alerts/Alert'
import Job from 'jobs/Job'
import { VoiceCallStatus } from 'messaging/calling/VoiceCall'
import VoiceCallStore from 'messaging/calling/VoiceCallStore'
import Conversation from 'messaging/models/Conversation'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { Device, Call as TwilioCall } from '@twilio/voice-sdk'
import { asAtom } from 'utils/mobx-util'
import { fromClock, shareLatest } from 'utils/rx-util'
import { Subject, asapScheduler, observeOn, take, takeUntil } from 'rxjs'
import CallDeviceManager from 'messaging/calling/CallDeviceManager'
import TwilioVoiceCallSettingsModalViewModel from './TwilioVoiceCallSettingsModal/TwilioVoiceCallSettingsModalViewModel'

export const VoiceCallSteps = {
  INITIAL: { completed: 0, pending: 20 },
  PREPARING: { completed: 20, pending: 20 },
  DIALING: { completed: 20, pending: 20 },
  IN_PROGRESS: { completed: 20, pending: 20 },
  COMPLETE_OR_PREPARE: { completed: 80, pending: 20 },
} as const

export type VoiceCallStepsIds = keyof typeof VoiceCallSteps

export default class VoiceCallViewModel {
  @observable currentCallId: string | null = null
  @observable callMuted = false

  @observable.ref twilioDevice: Device | null = null
  @observable.ref twilioCall: TwilioCall | null = null

  /**
   * Dispose backing subject.
   * @private
   */
  private readonly _disposed = new Subject()

  /**
   * Emits when the VN is disposed.
   * Used as a stream terminator.
   */
  readonly disposed$ = this._disposed.pipe(
    shareLatest(),
    take(1),
    // Schedule emissions on the microtask queue.
    // Otherwise, if a subscriber calls `dispose`
    // from a subscription of a stream that uses
    // takeUntil(disposed$), the other subscribers will
    // not get notified.
    observeOn(asapScheduler)
  )

  private readonly $now = asAtom(
    fromClock(this.getNow).pipe(takeUntil(this.disposed$)),
    this.getNow()
  )

  public voiceCallSettingsModalVM = new TwilioVoiceCallSettingsModalViewModel(
    this.deviceManager
  )

  constructor(
    private job: Job,
    private voiceCallStore: VoiceCallStore,
    private newAlert: AlertFactory,
    private deviceManager: CallDeviceManager
  ) {
    makeObservable(this)
    this.voiceCallStore.events$.subscribe()
  }

  private getNow() {
    return new Date()
  }

  /**
   * Duration in milliseconds.
   */
  @computed
  get duration() {
    if (!this.currentCall) {
      return 0
    }

    const relative = this.currentCall.dateCompleted ?? this.$now()
    const startDate =
      this.currentCall.dateStarted ?? this.currentCall.dateCreated
    const absolute = relative.valueOf() - startDate.valueOf()
    // Round to the nearest second.
    return absolute - (absolute % 1000)
  }

  /**
   * The conversation for the job this voice call lives in
   */
  @computed
  get conversation() {
    return this.job.conversation ?? Conversation.none
  }

  /**
   * All the calls for this conversation
   */
  @computed
  get calls() {
    return this.voiceCallStore.callsForConversation(this.conversation.id)
  }

  /**
   * The completed calls for this conversation
   */
  @computed
  get completedCalls() {
    return this.calls.filter((c) => c.completed)
  }

  /**
   * The relevant current call.
   */
  @computed
  get currentCall() {
    const currentCall = this.voiceCallStore.callById(this.currentCallId)
    return currentCall
  }

  @computed
  get currentCallStatus() {
    return this.currentCall?.status ?? VoiceCallStatus.Prepared
  }

  /**
   * The step in the call cycle we're in.
   * A conversation can have many calls. Once a call is
   * completed another can be prepared.
   */
  @computed
  get callStep(): VoiceCallStepsIds {
    if (!this.currentCall) {
      if (this.completedCalls.length > 0) {
        return 'COMPLETE_OR_PREPARE'
      }
      return 'INITIAL'
    }

    if (this.currentCall.prepared) {
      return 'PREPARING'
    }

    if (this.currentCall.connecting || this.currentCall.ringing) {
      return 'DIALING'
    }

    if (this.currentCall.inProgress) {
      return 'IN_PROGRESS'
    }

    if (this.currentCall.completed) {
      return 'COMPLETE_OR_PREPARE'
    }

    return 'INITIAL'
  }

  /**
   * Canceling the prepared call simply resets
   * the twilio calling infra
   */
  @action.bound
  cancelPreparation() {
    this.reset()
  }

  /**
   * Disposes the twilio device
   */
  @action
  private disposeDevice() {
    this.twilioDevice?.disconnectAll()
    this.twilioDevice?.audio?.unsetInputDevice()
    this.twilioDevice?.destroy()
  }

  /**
   * Resets the call state.
   * Idempotent oprations
   */
  @action
  reset() {
    this.twilioCall?.disconnect()
    this.twilioCall = null
    this.disposeDevice()
    this._disposed.next(undefined)
    this.currentCallId = null
  }

  @action
  setCurrentCallId(callId: string | null) {
    this.currentCallId = callId
  }

  /**
   * Prepares a call and the twilio device with
   * the token returned from that call.
   *
   * Sets that call as the current active one.
   */
  prepareCall = task.resolved(async () => {
    const clientChampionUserId = this.job.championClient?.member.user.publicId

    await this.voiceCallStore
      .prepareCall(this.conversation.id, Id.assert(clientChampionUserId))
      .then(async (preparedCall) => {
        const twilioToken = preparedCall.twilioToken
        const device = new Device(twilioToken, {
          logLevel: 'INFO',
          codecPreferences: [TwilioCall.Codec.Opus, TwilioCall.Codec.PCMU],
          tokenRefreshMs: 2000,
        })

        device.on('registered', async () => {
          this.twilioDevice = device
          this.setCurrentCallId(Id.assert(preparedCall.callId))
          await this.voiceCallSettingsModalVM.activate(device)
        })

        await device.register()
      })
      .catch((error) => {
        this.newAlert().fromError(error).show()
        console.error(error)
        this.reset()
      })
  })

  /**
   * Starts the call, dialing the call recipient.
   * The twilio device drives the state and
   * is reconciled with realtime events.
   */
  startCall = task.resolved(async () => {
    if (!this.twilioDevice || !this.currentCall) {
      this.newAlert().fromError(
        new Error("Couldn't start call. Please try preparing it again.")
      )
      this.reset()
      return
    }

    const voiceCall = this.currentCall
    const call = await this.twilioDevice.connect({
      params: {
        callId: Id.assert(voiceCall.id),
        conversationId: voiceCall.conversationId,
      },
    })

    // Triggers as soon as dialing starts so we mark the call as connecting.
    // For our purposes this doesn't necessarily means the call was picked up.
    call.on('accept', () => {
      this.twilioCall = call
      this.currentCall?.setCallStatus(VoiceCallStatus.Connecting)
    })
    call.on('disconnect', () => this.reset())
    call.on('cancel', () => this.reset())
  })

  /**
   * Tells the twilio device to disconnnect the call.
   * Realtime events should update the current call state,
   * but we optimistically update it as well.
   */
  completeCall = task.resolved(async () => {
    if (!this.twilioCall) {
      return
    }

    this.twilioCall.disconnect()
    this.currentCall?.setCallStatus(VoiceCallStatus.Completed)
    this.reset()
  })

  /**
   * Activates the view model.
   * Fetches the calls for this conversation
   */
  activate = task.resolved(async () => {
    this.disposeDevice()
    await this.voiceCallStore
      .fetchConversationCalls(this.conversation.id)
      .catch((e) => console.log(e))
  })

  /**
   * Toggles the microphone.
   */
  toggleMute = task.resolved(async () => {
    this.callMuted = !this.twilioCall?.isMuted()
    this.twilioCall?.mute(this.callMuted)
  })
}
